import { useCallback, useEffect, useState } from "react";
import {
    DEFAULT_INTERFACE_LANG,
    INTERFACE_LANG_KEYS,
    normalizeLangParam,
    resolveInterfaceLang,
} from "../../lib/interfaceLang";
import { getInterfaceLanguage, INTERFACE_LANG_CHANGED_EVENT, setInterfaceLanguage } from "../../services/LangPrefs";

const locales = {
    english: () => import("./en.json"),
    french: () => import("./fr.json"),
    chinese: () => import("./zh.json"),
    hindi: () => import("./hi.json"),
    spanish: () => import("./es.json"),
    arabic: () => import("./ar.json"),
    portuguese: () => import("./pt.json"),
    german: () => import("./de.json"),
    japanese: () => import("./ja.json"),
    korean: () => import("./ko.json"),
};

/**
 * Core packs: login, policy, terms, privacy. Reusable across projects.
 * Languages without a core pack fall back to core-en.json.
 */
const coreLocales = {
    english: () => import("./core-en.json"),
    french: () => import("./core-fr.json"),
    german: () => import("./core-de.json"),
    spanish: () => import("./core-es.json"),
    portuguese: () => import("./core-pt.json"),
    arabic: () => import("./core-ar.json"),
    japanese: () => import("./core-ja.json"),
    korean: () => import("./core-ko.json"),
    chinese: () => import("./core-zh.json"),
    hindi: () => import("./core-hi.json"),
};

function deepMerge(target, source) {
    const result = { ...target };
    for (const key of Object.keys(source)) {
        if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key]) &&
            target[key] && typeof target[key] === "object" && !Array.isArray(target[key])) {
            result[key] = deepMerge(target[key], source[key]);
        } else {
            result[key] = source[key];
        }
    }
    return result;
}

const SUPPORTED_LANGS = INTERFACE_LANG_KEYS;
const DEFAULT_LANG = DEFAULT_INTERFACE_LANG;

export { normalizeLangParam, resolveInterfaceLang } from "../../lib/interfaceLang";

let messagesCache = {};
let currentLang = null;

// Dev: drop stale locale cache when this module or locale JSON hot-reloads.
if (typeof module !== "undefined" && module.hot) {
    const clearLocaleCache = () => {
        messagesCache = {};
        currentLang = null;
    };
    module.hot.accept(clearLocaleCache);
    module.hot.accept(
        [
            "./en.json",
            "./fr.json",
            "./zh.json",
            "./hi.json",
            "./es.json",
            "./ar.json",
            "./pt.json",
            "./de.json",
            "./ja.json",
            "./ko.json",
        ],
        clearLocaleCache,
    );
}

/** Synchronously returns cached messages if available. Used to avoid flash of untranslated keys on first render. */
export function getCachedMessages(lang) {
    const effectiveLang = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
    return messagesCache[effectiveLang] || null;
}

/** Preloads the interface language so translations are ready before first paint. Call before rendering the app. */
export async function ensureLocaleReady() {
    const lang = resolveInterfaceLang(getInterfaceLanguage());
    await loadMessages(lang);
}

export async function loadMessages(lang) {
    const effectiveLang = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
    if (messagesCache[effectiveLang]) return messagesCache[effectiveLang];
    try {
        const loader = locales[effectiveLang] || locales[DEFAULT_LANG];
        const mod = await loader();
        let messages = mod.default;
        const coreLoader = coreLocales[effectiveLang] || coreLocales[DEFAULT_LANG];
        try {
            const coreMod = await coreLoader();
            const coreMessages = coreMod.default;
            if (coreMessages) messages = deepMerge(messages, coreMessages);
        } catch (coreErr) {
            if (effectiveLang !== DEFAULT_LANG) {
                const defaultCore = await coreLocales[DEFAULT_LANG]();
                const defaultCoreMessages = defaultCore.default;
                if (defaultCoreMessages) messages = deepMerge(messages, defaultCoreMessages);
            }
        }
        try {
            const enMod = await locales[DEFAULT_LANG]();
            const enWod = enMod.default?.wordOfDay;
            if (enWod) {
                const localized =
                    messages.wordOfDay && typeof messages.wordOfDay === "object" ? messages.wordOfDay : {};
                messages = { ...messages, wordOfDay: deepMerge({ ...enWod }, localized) };
            }
        } catch {
            /* keep messages as-is */
        }
        messagesCache[effectiveLang] = messages;
        return messages;
    } catch (e) {
        console.error(`Failed to load locale ${effectiveLang}:`, e);
        if (messagesCache[DEFAULT_LANG]) return messagesCache[DEFAULT_LANG];
        const mod = await locales[DEFAULT_LANG]();
        messagesCache[DEFAULT_LANG] = mod.default;
        return mod.default;
    }
}

function getNested(obj, path) {
    const keys = path.split(".");
    let val = obj;
    for (const k of keys) {
        val = val?.[k];
        if (val === undefined) return undefined;
    }
    return val;
}

function interpolate(str, params = {}) {
    if (typeof str !== "string") return str;
    return str.replace(/\{\{(\w+)\}\}/g, (_, key) =>
        params[key] !== undefined ? String(params[key]) : `{{${key}}}`
    );
}

/**
 * Translate a key using a given message object (e.g. from loadMessages or getCachedMessages).
 * For previews where the selected language’s JSON should drive copy instead of the active UI pack.
 * @param {Record<string, unknown>|null|undefined} messages
 * @param {string} key
 * @param {Record<string, string|number>|string|undefined} [paramsOrFallback]
 * @returns {string}
 */
export function translateFromMessages(messages, key, paramsOrFallback) {
    const { params, fallback } = normalizeTArgs(paramsOrFallback);
    if (!messages) return fallback ?? key;
    const val = getNested(messages, key);
    if (val !== undefined) return interpolate(val, params);
    if (fallback !== undefined) return interpolate(fallback, params);
    return key;
}

function normalizeTArgs(paramsOrFallback) {
    if (typeof paramsOrFallback === "string") {
        return { params: {}, fallback: paramsOrFallback };
    }
    if (paramsOrFallback && typeof paramsOrFallback === "object") {
        // Support the i18next `{ defaultValue }` convention while still using the
        // remaining keys as interpolation params.
        const { defaultValue, ...params } = paramsOrFallback;
        return {
            params,
            fallback: typeof defaultValue === "string" ? defaultValue : undefined,
        };
    }
    return { params: {}, fallback: undefined };
}

/**
 * Hook for pages i18n. Uses ui.lang (getInterfaceLanguage) unless overrideLang is provided.
 * When overrideLang is provided (e.g. from URL ?lang= param), it takes high priority.
 * @param {string|null|undefined} [overrideLang] - Optional language override (e.g. from URL). Use normalizeLangParam() to validate.
 * @returns {{ t: (key: string, params?: object|string) => string, lang: string, setLang: (lang: string) => void, loading: boolean }}
 */
export function usePagesTranslation(overrideLang) {
    const baseLang = resolveInterfaceLang(getInterfaceLanguage());
    const effectiveOverride = overrideLang ? resolveInterfaceLang(overrideLang) : null;
    const effectiveInitial = effectiveOverride ?? baseLang;
    const initialCached = getCachedMessages(effectiveInitial);
    const [lang, setLangState] = useState(effectiveInitial);
    const [messages, setMessages] = useState(initialCached);
    const [loading, setLoading] = useState(!initialCached);
    const hasOverride = Boolean(overrideLang && effectiveOverride);

    useEffect(() => {
        const targetLang = hasOverride ? effectiveOverride : resolveInterfaceLang(getInterfaceLanguage());
        setLangState(targetLang);
    }, [overrideLang, hasOverride, effectiveOverride]);

    useEffect(() => {
        let cancelled = false;
        const effectiveLang = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
        if (messagesCache[effectiveLang]) {
            setMessages(messagesCache[effectiveLang]);
            setLoading(false);
            return;
        }
        setLoading(true);
        loadMessages(lang).then((msgs) => {
            if (!cancelled) {
                setMessages(msgs);
                currentLang = lang;
            }
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [lang]);

    useEffect(() => {
        if (hasOverride) return;
        const onLangChanged = (e) => {
            const newLang = resolveInterfaceLang(e?.detail?.language || getInterfaceLanguage());
            if (newLang !== lang) {
                setLangState(newLang);
            }
        };
        const onStorage = (e) => {
            if (e.key === "ui.lang" && e.newValue) {
                const newLang = resolveInterfaceLang(e.newValue);
                if (newLang !== lang) {
                    setLangState(newLang);
                }
            }
        };
        window.addEventListener(INTERFACE_LANG_CHANGED_EVENT, onLangChanged);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener(INTERFACE_LANG_CHANGED_EVENT, onLangChanged);
            window.removeEventListener("storage", onStorage);
        };
    }, [lang, hasOverride]);

    const t = useCallback(
        (key, paramsOrFallback) => {
            const { params, fallback } = normalizeTArgs(paramsOrFallback);
            if (!messages) return fallback ?? key;
            const val = getNested(messages, key);
            if (val !== undefined) return interpolate(val, params);
            const enMessages = getCachedMessages(DEFAULT_LANG);
            const enVal = enMessages ? getNested(enMessages, key) : undefined;
            if (enVal !== undefined) return interpolate(enVal, params);
            if (fallback !== undefined) return interpolate(fallback, params);
            return key;
        },
        [messages]
    );

    const setLang = useCallback((newLang) => {
        const resolved = normalizeLangParam(newLang);
        if (resolved) {
            setInterfaceLanguage(resolved);
            setLangState(resolved);
            currentLang = resolved;
        }
    }, []);

    return { t, lang, setLang, loading };
}

export { SUPPORTED_LANGS, DEFAULT_LANG };
